import React, { useState } from 'react';
import {
  Box, Typography, Button, LinearProgress, IconButton,
  Select, MenuItem, FormControl, InputLabel, TextField,
  Chip, Tooltip, Paper, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import axiosInstance from '../../axiosInstance';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

const COLLECTION_TYPES = [
  { key: 'games',          label: 'Games',          emoji: '🎮', flag: 'enableGamesFeature' },
  { key: 'consoles',       label: 'Consoles',        emoji: '🕹️', flag: 'enableConsolesFeature' },
  { key: 'lego',           label: 'LEGO',            emoji: '🧱', flag: 'enableLegoFeature' },
  { key: 'movies',         label: 'Movies',          emoji: '🎬', flag: 'enableMoviesFeature' },
  { key: 'music',          label: 'Music',           emoji: '🎵', flag: 'enableMusicFeature' },
  { key: 'books',          label: 'Books',           emoji: '📚', flag: 'enableBooksFeature' },
  { key: 'board-games',    label: 'Board Games',     emoji: '🎲', flag: 'enableBoardGamesFeature' },
  { key: 'trading-cards',  label: 'Trading Cards',   emoji: '🃏', flag: 'enableTradingCardsFeature' },
  { key: 'manga',          label: 'Manga',           emoji: '📖', flag: 'enableMangaFeature' },
  { key: 'comics',         label: 'Comics',          emoji: '💥', flag: 'enableComicsFeature' },
];

export default function AnnualGoals({ goals = [], year, onRefresh }) {
  const { featureFlags } = useFeatureFlags();
  const [adding, setAdding] = useState(false);
  const [newType, setNewType] = useState('');
  const [newTarget, setNewTarget] = useState('');
  const [saving, setSaving] = useState(false);

  const enabledTypes = COLLECTION_TYPES.filter(t => featureFlags[t.flag] !== false);
  const activeTypes = new Set(goals.map(g => g.type));
  const availableTypes = enabledTypes.filter(t => !activeTypes.has(t.key));

  const handleAdd = async () => {
    const targetNum = parseInt(newTarget);
    if (!newType || !targetNum || targetNum < 1) return;
    setSaving(true);
    try {
      await axiosInstance.put('/users/me/goals', { type: newType, target: targetNum, year });
      setAdding(false);
      setNewType('');
      setNewTarget('');
      onRefresh();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type) => {
    await axiosInstance.delete('/users/me/goals', { data: { type, year } });
    onRefresh();
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">{year} Goals</Typography>
        {!adding && availableTypes.length > 0 && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setAdding(true)}>Add Goal</Button>
        )}
      </Box>

      {goals.length === 0 && !adding && (
        <Typography variant="body2" color="text.secondary">
          No goals set yet. Add a goal to track how many items you collect this year.
        </Typography>
      )}

      {goals.map(g => {
        const typeInfo = COLLECTION_TYPES.find(t => t.key === g.type) || { emoji: '📦', label: g.type };
        const pct = Math.min(g.pct, 100);
        const complete = g.current >= g.target;
        return (
          <Box key={g.type} sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">{typeInfo.emoji} {typeInfo.label}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {complete && (
                  <Chip icon={<EmojiEventsIcon />} label="Goal reached!" size="small" color="success" />
                )}
                <Typography variant="caption" color="text.secondary">{g.current} / {g.target}</Typography>
                <Tooltip title="Remove goal">
                  <IconButton size="small" onClick={() => handleDelete(g.type)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={pct}
              color={complete ? 'success' : 'primary'}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        );
      })}

      {adding && (
        <>
          {goals.length > 0 && <Divider sx={{ my: 1.5 }} />}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Collection</InputLabel>
              <Select value={newType} label="Collection" onChange={e => setNewType(e.target.value)}>
                {availableTypes.map(t => (
                  <MenuItem key={t.key} value={t.key}>{t.emoji} {t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Target"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={newTarget}
              onChange={e => setNewTarget(e.target.value)}
              sx={{ width: 90 }}
            />
            <Button
              size="small"
              variant="contained"
              disabled={saving || !newType || !newTarget || parseInt(newTarget) < 1}
              onClick={handleAdd}
            >
              Save
            </Button>
            <Button size="small" onClick={() => { setAdding(false); setNewType(''); setNewTarget(''); }}>
              Cancel
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
}
